import moment from 'moment-timezone';
import 'moment/locale/fr';
import i18n from '../locales/i18n';
import { TimezoneType } from '../modules/profile/ProfileTypes';

type Lang = 'en' | 'fr';
export type DateFormatType = 'full' | 'noYear' | 'DayMonthDate';

/**
 * Provides consistent date and optional time formatting based on application language and browser locale.
 *
 * @param date The date string, number, or Date object to format.
 * @param formatType Specifies whether to include the year ('full'), not ('noYear'), or Day, Month, Date ('DayMonthDate').
 * @param formatTimeAs An optional Moment.js format string for time (e.g., 'hh:mm A'), or 'time' for default, or 'at time' for "at H:MM AM/PM" (or "à HH:MM").
 * @param fallback The string to return if the date is invalid or null/undefined. Defaults to an empty string.
 * @param returnFormatStringOnly If true, returns only the Moment.js format string instead of the formatted date. Defaults to false.
 * @param timezone Optional TimezoneType object. Uses `name` (IANA tz, e.g. "America/New_York") first,
 *                 falls back to `offset` (e.g. "+05:45") if no valid IANA name is provided.
 *                 When omitted, the local system timezone is used.
 * @returns A consistently formatted date and/or time string, or just the format string if `returnFormatStringOnly` is true.
 *
 * Examples:
 * // For appLang: "fr", browserLocale: "fr-FR"
 * // full date: "15 février 2025"
 * // full date with time: "15 février 2025 à 10:30"
 * // partial date: "21 févr"
 * // partial date with time: "21 févr. 10:30 AM"
 * // DayMonthDate: "ven. 15 févr."
 * // DayMonthDate at time: "ven. 15 févr. à 10:30"
 *
 * // For appLang: "en", browserLocale: "en-US"
 * // full date: "Feb 15, 2025"
 * // full date with time: "Feb 15, 2025 at 10:30 AM"
 * // partial date: "Feb 21"
 * // partial date with time: "Feb 21 10:30 AM"
 * // DayMonthDate: "Fri Feb 15"
 * // DayMonthDate at time: "Fri Feb 15 at 10:30 AM"
 *
 * // With timezone: { name: "America/New_York" }
 * // Converts and displays the date/time in Eastern Time regardless of system locale.
 *
 * // With timezone: { offset: "+05:45" }
 * // Falls back to fixed offset (e.g. Nepal Time) when no IANA name is available.
 */
export function getConsistentDateFormat(
  date: string | number | Date | null | undefined,
  formatType: DateFormatType,
  formatTimeAs?: 'time' | 'atTime' | string,
  fallback = '',
  returnFormatStringOnly = false,
  timezone?: TimezoneType,
): string {
  if (!date) return fallback;

  const appLang: Lang = i18n.language as Lang;
  const browserLocale: string = typeof navigator !== 'undefined' ? navigator.language : 'en-US';
  const isEnUS = browserLocale.toLowerCase() === 'en-us';

  // ------------------------------------------------------------------
  // Build a moment instance in the requested timezone.
  //
  // Priority:
  //   1. timezone.name  — IANA identifier (e.g. "America/New_York")
  //                       Validated via moment.tz.zone(); falls through
  //                       to offset if the name is unknown/invalid.
  //   2. timezone.offset — Fixed UTC offset string (e.g. "+05:45")
  //                        Parsed with moment.parseZone so the offset is
  //                        preserved exactly rather than converted.
  //   3. No timezone    — moment(date) uses the system/local timezone,
  //                       preserving the original behaviour.
  // ------------------------------------------------------------------
  let m: moment.Moment;

  if (timezone?.name && moment.tz.zone(timezone.name)) {
    // Valid IANA timezone name — convert to that zone
    m = moment.tz(date, timezone.name);
  } else if (timezone?.offset) {
    // Fixed offset fallback — parse preserving the given offset
    m = moment.parseZone(`${moment(date).format('YYYY-MM-DDTHH:mm:ss')}${timezone.offset}`);
  } else {
    // No timezone supplied — use local time (original behaviour)
    m = moment(date);
  }

  if (!m.isValid()) return fallback;

  let finalFormatString: string;

  if (appLang === 'fr') {
    m.locale('fr');
    if (formatType === 'full') {
      finalFormatString = 'D MMM YYYY';
    } else if (formatType === 'noYear') {
      finalFormatString = 'D MMM';
    } else if (formatType === 'DayMonthDate') {
      finalFormatString = 'ddd D MMM';
    } else {
      finalFormatString = 'D MMM'; // Fallback
    }
  } else {
    // English locales
    m.locale('en');
    if (formatType === 'full') {
      finalFormatString = isEnUS ? 'MMM D, YYYY' : 'D MMM YYYY';
    } else if (formatType === 'noYear') {
      finalFormatString = isEnUS ? 'MMM D' : 'D MMM';
    } else if (formatType === 'DayMonthDate') {
      finalFormatString = isEnUS ? 'ddd MMM D' : 'ddd D MMM';
    } else {
      finalFormatString = 'MMM D'; // Fallback
    }
  }

  if (formatTimeAs === 'atTime') {
    finalFormatString += appLang === 'fr' ? ' [à] HH:mm' : ' [at] h:mm A';
  } else if (formatTimeAs === 'time') {
    finalFormatString += ' h:mm A';
  } else if (formatTimeAs) {
    finalFormatString += ` ${formatTimeAs}`;
  }

  if (returnFormatStringOnly) {
    return finalFormatString;
  }

  return m.format(finalFormatString);
}
